import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "settings-page" }
const _hoisted_2 = { class: "settings-page__header" }
const _hoisted_3 = { class: "settings-page__grid" }
const _hoisted_4 = { class: "settings-panel settings-profile-panel" }
const _hoisted_5 = { class: "settings-profile-card" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "settings-panel settings-preferences-panel" }
const _hoisted_8 = { class: "settings-field" }
const _hoisted_9 = { class: "settings-field" }
const _hoisted_10 = {
  id: "notifications",
  class: "settings-panel settings-notifications-panel"
}
const _hoisted_11 = { class: "settings-panel__header settings-panel__header--split" }
const _hoisted_12 = { class: "settings-notifications-panel__device" }
const _hoisted_13 = ["checked", "disabled", "onChange"]
const _hoisted_14 = {
  key: 1,
  class: "settings-panel settings-api-panel"
}
const _hoisted_15 = { class: "settings-panel__header settings-panel__header--split" }
const _hoisted_16 = {
  class: "settings-api-tabs",
  role: "tablist",
  "aria-label": "API key status"
}
const _hoisted_17 = ["aria-selected"]
const _hoisted_18 = ["aria-selected"]
const _hoisted_19 = {
  key: 0,
  class: "settings-token-reveal",
  role: "status"
}
const _hoisted_20 = { class: "settings-field settings-field--grow" }
const _hoisted_21 = { class: "settings-field settings-field--compact" }
const _hoisted_22 = { class: "settings-field settings-field--compact" }
const _hoisted_23 = {
  key: 1,
  class: "settings-empty-state"
}
const _hoisted_24 = {
  key: 2,
  class: "settings-empty-state"
}
const _hoisted_25 = {
  key: 3,
  class: "settings-api-table-wrap"
}
const _hoisted_26 = { class: "settings-api-table" }
const _hoisted_27 = {
  key: 0,
  class: "settings-api-table__actions"
}
const _hoisted_28 = {
  key: 0,
  class: "settings-api-table__actions"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';

import {
  refreshCurrentUserProfile,
  getCurrentUser,
  updateCurrentUserPreferences,
  USER_PROFILE_UPDATED_EVENT,
  type CachedUser
} from '@/modules/firebase/Auth';
import { functions } from '@/modules/firebase';
import { getThemeMode, setThemeMode, subscribeTheme, type ThemeMode } from '@/modules/ui/theme';
import { Button, Input, Select, Tag } from '@/components/ui';
import type { SelectOption } from '@/components/ui';
import type { Unsubscribe } from 'firebase/firestore';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  saveNotificationPreferences,
  subscribeNotificationPreferences,
  type NotificationPreferences
} from '@/modules/notifications/NotificationClient';
import {
  disableNotificationPush,
  enableNotificationPush,
  getPushPermissionState,
  isNotificationPushEnabled
} from '@/modules/notifications/PushNotifications';

type ApiKeyStatus = 'active' | 'deleted';

interface ApiKeyRateLimit {
  enabled: boolean;
  perMinute: number;
  perDay: number;
  burst: number;
}

interface ApiKeyMetadata {
  keyId: string;
  ownerUid: string;
  name: string;
  tokenPrefix: string;
  tokenSuffix: string;
  status: ApiKeyStatus;
  rateLimit: ApiKeyRateLimit;
  createdAt: unknown;
  modifiedAt: unknown;
  rotatedAt?: unknown;
  deletedAt?: unknown;
  lastUsedAt?: unknown;
  lastUsedPath?: string | null;
  lastUsedMethod?: string | null;
  requestCount: number;
}

interface CallableEnvelope<T> {
  success: boolean;
  data: T;
}

const DEFAULT_LIST_LENGTH = 10;

export default /*@__PURE__*/_defineComponent({
  __name: 'SettingsPage',
  setup(__props) {

const listApiKeysCallable = httpsCallable<undefined, CallableEnvelope<{ apiKeys: ApiKeyMetadata[] }>>(functions, 'listApiKeys');
const createApiKeyCallable = httpsCallable<Record<string, unknown>, CallableEnvelope<{ token: string; apiKey: ApiKeyMetadata }>>(functions, 'createApiKey');
const rotateApiKeyCallable = httpsCallable<{ keyId: string }, CallableEnvelope<{ token: string; apiKey: ApiKeyMetadata }>>(functions, 'rotateApiKey');
const deleteApiKeyCallable = httpsCallable<{ keyId: string }, CallableEnvelope<{ apiKey: ApiKeyMetadata }>>(functions, 'deleteApiKey');

const currentUser = ref<CachedUser | null>(getCurrentUser());
const isProfileReady = ref(false);
const isRefreshingProfile = ref(false);
const isLoadingKeys = ref(false);
const isCreatingKey = ref(false);
const actionKeyId = ref<string | null>(null);
const apiKeys = ref<ApiKeyMetadata[]>([]);
const apiKeyView = ref<'active' | 'archived'>('active');
const newKeyName = ref('Postman testing');
const rateLimitPerMinute = ref('60');
const rateLimitPerDay = ref('1000');
const newToken = ref('');
const statusMessage = ref('');
const statusTone = ref<'success' | 'danger' | 'neutral'>('neutral');
const themeSelection = ref<ThemeMode>(getThemeMode());
const isSavingListLength = ref(false);
const notificationPreferences = ref<NotificationPreferences>({ ...DEFAULT_NOTIFICATION_PREFERENCES });
const isSavingNotifications = ref(false);
const isChangingPush = ref(false);
const pushPermission = ref<NotificationPermission | 'unsupported'>(getPushPermissionState());
const pushEnabled = ref(isNotificationPushEnabled());

const VALID_LIST_LENGTHS = [10, 15, 20] as const;

const themeOptions: SelectOption[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
];

const listLengthOptions: SelectOption[] = VALID_LIST_LENGTHS.map(value => ({
  value: String(value),
  label: `${value} rows`
}));

const hasApiAccess = computed(() => isProfileReady.value && (
  currentUser.value?.permissions?.apiAccess === true || currentUser.value?.permissions?.adminAccess === true
));
const profilePhoto = computed(() => currentUser.value?.slack?.avatar || currentUser.value?.photoURL || '/assets/img/avatar.png');
const defaultListLengthSelection = ref(String(resolveDefaultListLength()));
const activeApiKeys = computed(() => apiKeys.value.filter(key => key.status === 'active'));
const archivedApiKeys = computed(() => apiKeys.value.filter(key => key.status === 'deleted'));
const visibleApiKeys = computed(() => apiKeyView.value === 'active' ? activeApiKeys.value : archivedApiKeys.value);
let statusTimeout: ReturnType<typeof setTimeout> | null = null;
let unsubscribeTheme: (() => void) | null = null;
let unsubscribeNotificationPreferences: Unsubscribe | null = null;

const notificationPreferenceOptions: Array<{ key: keyof NotificationPreferences; label: string; description: string }> = [
  { key: 'pushMentions', label: 'Browser mention alerts', description: 'Show a private browser or PWA alert for mentions.' },
  { key: 'pushStarredActivity', label: 'New messages on starred shipments', description: 'Show an alert when someone posts a human activity-feed message on a shipment you starred.' },
  { key: 'pushShipmentMilestones', label: 'Tracking milestones on starred shipments', description: 'Show an alert when a starred shipment is out for delivery, delivered, has an exception, or is returned.' }
];

const pushPermissionLabel = computed(() => {
  if (pushEnabled.value) return 'Enabled on this device';
  if (pushPermission.value === 'denied') return 'Blocked by browser settings';
  if (pushPermission.value === 'unsupported') return 'Not supported';
  return 'Not enabled';
});

async function toggleNotificationPreference(key: keyof NotificationPreferences, event: Event): Promise<void> {
  const checked = (event.target as HTMLInputElement).checked;
  const previous = notificationPreferences.value[key];
  notificationPreferences.value = { ...notificationPreferences.value, [key]: checked };
  isSavingNotifications.value = true;
  try {
    notificationPreferences.value = await saveNotificationPreferences({ [key]: checked });
    setStatus('Notification preference saved.', 'success', 3000);
  } catch (error) {
    notificationPreferences.value = { ...notificationPreferences.value, [key]: previous };
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isSavingNotifications.value = false;
  }
}

async function enablePush(): Promise<void> {
  isChangingPush.value = true;
  try {
    const result = await enableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = result === 'enabled';
    setStatus(result === 'enabled' ? 'Browser notifications enabled.' : result === 'denied'
      ? 'Notification permission was not granted.' : 'This browser does not support notifications.', result === 'enabled' ? 'success' : 'neutral', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isChangingPush.value = false;
  }
}

async function disablePush(): Promise<void> {
  isChangingPush.value = true;
  try {
    await disableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = false;
    setStatus('Browser notifications disabled for this device.', 'success', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isChangingPush.value = false;
  }
}

function handleUserProfileUpdated(event: Event): void {
  currentUser.value = (event as CustomEvent<CachedUser>).detail;
  isProfileReady.value = true;
}

watch(themeSelection, (mode) => {
  if (mode !== getThemeMode()) {
    setThemeMode(mode);
  }
});

watch(defaultListLengthSelection, (value) => {
  void saveDefaultListLength(value);
});

function normalizeListLength(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return VALID_LIST_LENGTHS.includes(parsed as typeof VALID_LIST_LENGTHS[number]) ? parsed : null;
}

function resolveDefaultListLength(): number {
  const remotePreference = normalizeListLength(currentUser.value?.preferences?.defaultListLength);
  if (remotePreference) {
    return remotePreference;
  }

  try {
    return normalizeListLength(localStorage.getItem('shipments_pageLength')) || DEFAULT_LIST_LENGTH;
  } catch {
    return DEFAULT_LIST_LENGTH;
  }
}

async function saveDefaultListLength(value: string): Promise<void> {
  const listLength = normalizeListLength(value);
  const cachedUser = currentUser.value;

  if (!listLength || !cachedUser?.uid) {
    return;
  }

  isSavingListLength.value = true;

  try {
    const updatedUser = await updateCurrentUserPreferences({ defaultListLength: listLength });

    currentUser.value = updatedUser;
    localStorage.setItem('USER_KEY', JSON.stringify(updatedUser));
    localStorage.setItem('shipments_pageLength', String(listLength));
    setStatus('Default list length saved.', 'success', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isSavingListLength.value = false;
  }
}

function setStatus(message: string, tone: 'success' | 'danger' | 'neutral' = 'neutral', durationMs = 0): void {
  if (statusTimeout) {
    clearTimeout(statusTimeout);
    statusTimeout = null;
  }

  statusMessage.value = message;
  statusTone.value = tone;

  if (durationMs > 0) {
    statusTimeout = setTimeout(() => {
      statusMessage.value = '';
      statusTimeout = null;
    }, durationMs);
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Something went wrong.';
}

function parsePositiveInteger(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

async function refreshProfile(showSuccess: boolean): Promise<void> {
  isRefreshingProfile.value = true;
  isProfileReady.value = false;

  try {
    await refreshCurrentUserProfile();
    currentUser.value = getCurrentUser();
    isProfileReady.value = true;
    const remoteListLength = normalizeListLength(currentUser.value?.preferences?.defaultListLength);
    if (remoteListLength) {
      defaultListLengthSelection.value = String(remoteListLength);
    }
    if (showSuccess) {
      setStatus('Profile refreshed.', 'success', 5000);
    }
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isRefreshingProfile.value = false;
  }
}

async function loadApiKeys(): Promise<void> {
  if (!hasApiAccess.value) {
    return;
  }

  isLoadingKeys.value = true;

  try {
    const result = await listApiKeysCallable(undefined);
    apiKeys.value = result.data.data.apiKeys;
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isLoadingKeys.value = false;
  }
}

async function createKey(): Promise<void> {
  if (!hasApiAccess.value || !newKeyName.value.trim()) {
    return;
  }

  isCreatingKey.value = true;
  newToken.value = '';

  try {
    const result = await createApiKeyCallable({
      name: newKeyName.value.trim(),
      rateLimit: {
        enabled: true,
        perMinute: parsePositiveInteger(rateLimitPerMinute.value, 60),
        perDay: parsePositiveInteger(rateLimitPerDay.value, 1000),
        burst: 20
      }
    });

    newToken.value = result.data.data.token;
    setStatus('API key created. Copy the token before leaving this page.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isCreatingKey.value = false;
  }
}

async function rotateKey(keyId: string): Promise<void> {
  if (!window.confirm('Rotate this API key? Existing integrations using the old token will stop working.')) {
    return;
  }

  actionKeyId.value = keyId;
  newToken.value = '';

  try {
    const result = await rotateApiKeyCallable({ keyId });
    newToken.value = result.data.data.token;
    setStatus('API key rotated. Copy the new token before leaving this page.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    actionKeyId.value = null;
  }
}

async function deleteKey(keyId: string): Promise<void> {
  if (!window.confirm('Revoke this API key? This keeps audit history but blocks future use.')) {
    return;
  }

  actionKeyId.value = keyId;

  try {
    await deleteApiKeyCallable({ keyId });
    setStatus('API key revoked.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    actionKeyId.value = null;
  }
}

async function copyToken(): Promise<void> {
  if (!newToken.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(newToken.value);
    setStatus('Token copied.', 'success');
  } catch {
    setStatus('Copy failed. Select the token and copy it manually.', 'danger');
  }
}

function formatDate(value: unknown): string {
  if (!value) {
    return 'Never';
  }

  let date: Date | null = null;

  if (typeof value === 'object' && value !== null && 'seconds' in value && typeof (value as { seconds?: unknown }).seconds === 'number') {
    date = new Date((value as { seconds: number }).seconds * 1000);
  } else if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    date = (value as { toDate: () => Date }).toDate();
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

onMounted(async () => {
  window.addEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
  unsubscribeTheme = subscribeTheme(({ mode }) => {
    themeSelection.value = mode;
  });

  await refreshProfile(false);

  if (currentUser.value?.uid) {
    unsubscribeNotificationPreferences = subscribeNotificationPreferences(currentUser.value.uid, preferences => {
      notificationPreferences.value = preferences;
    }, error => setStatus(getErrorMessage(error), 'danger'));
  }

  await loadApiKeys();
});

onBeforeUnmount(() => {
  window.removeEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
  if (statusTimeout) {
    clearTimeout(statusTimeout);
  }
  unsubscribeTheme?.();
  unsubscribeNotificationPreferences?.();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[10] || (_cache[10] = _createElementVNode("div", null, [
        _createElementVNode("p", { class: "settings-page__eyebrow" }, "Account"),
        _createElementVNode("h1", null, "Settings"),
        _createElementVNode("p", { class: "settings-page__subtitle" }, "Manage your profile and synced interface preferences.")
      ], -1 /* CACHED */)),
      _createVNode(_unref(Button), {
        variant: "soft",
        tone: "neutral",
        size: "sm",
        disabled: isRefreshingProfile.value,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (refreshProfile(true)))
      }, {
        leading: _withCtx(() => [...(_cache[8] || (_cache[8] = [
          _createElementVNode("i", {
            class: "material-icons",
            "aria-hidden": "true"
          }, "sync", -1 /* CACHED */)
        ]))]),
        default: _withCtx(() => [
          _cache[9] || (_cache[9] = _createTextVNode(" Refresh profile ", -1 /* CACHED */))
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["disabled"])
    ]),
    (statusMessage.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["settings-status", `settings-status--${statusTone.value}`])
        }, _toDisplayString(statusMessage.value), 3 /* TEXT, CLASS */))
      : _createCommentVNode("v-if", true),
    _createElementVNode("section", _hoisted_3, [
      _createElementVNode("article", _hoisted_4, [
        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "settings-panel__header" }, [
          _createElementVNode("div", null, [
            _createElementVNode("h2", null, "Profile"),
            _createElementVNode("p", null, "Your current account information.")
          ])
        ], -1 /* CACHED */)),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("img", {
            src: profilePhoto.value,
            alt: "User avatar",
            class: "settings-profile-card__avatar"
          }, null, 8 /* PROPS */, _hoisted_6),
          _createElementVNode("div", null, [
            _createElementVNode("strong", null, _toDisplayString(currentUser.value?.displayName || 'User'), 1 /* TEXT */),
            _createElementVNode("span", null, _toDisplayString(currentUser.value?.email || 'Email unavailable'), 1 /* TEXT */)
          ])
        ])
      ]),
      _createElementVNode("article", _hoisted_7, [
        _cache[14] || (_cache[14] = _createElementVNode("div", { class: "settings-panel__header" }, [
          _createElementVNode("div", null, [
            _createElementVNode("h2", null, "Preferences"),
            _createElementVNode("p", null, "Interface options synced across your devices.")
          ])
        ], -1 /* CACHED */)),
        _createElementVNode("label", _hoisted_8, [
          _cache[12] || (_cache[12] = _createElementVNode("span", null, "Theme", -1 /* CACHED */)),
          _createVNode(_unref(Select), {
            modelValue: themeSelection.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((themeSelection).value = $event)),
            options: themeOptions,
            variant: "form",
            "aria-label": "Theme preference"
          }, null, 8 /* PROPS */, ["modelValue"])
        ]),
        _createElementVNode("label", _hoisted_9, [
          _cache[13] || (_cache[13] = _createElementVNode("span", null, "Default list length", -1 /* CACHED */)),
          _createVNode(_unref(Select), {
            modelValue: defaultListLengthSelection.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((defaultListLengthSelection).value = $event)),
            options: _unref(listLengthOptions),
            variant: "form",
            "aria-label": "Default shipment list length",
            disabled: isSavingListLength.value
          }, null, 8 /* PROPS */, ["modelValue", "options", "disabled"])
        ])
      ]),
      _createElementVNode("article", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          _cache[17] || (_cache[17] = _createElementVNode("div", null, [
            _createElementVNode("h2", null, "Notifications"),
            _createElementVNode("p", null, "Choose which browser and PWA alerts you receive. Slack DMs are always sent for direct @mentions.")
          ], -1 /* CACHED */)),
          (!pushEnabled.value)
            ? (_openBlock(), _createBlock(_unref(Button), {
                key: 0,
                variant: "soft",
                tone: "primary",
                size: "sm",
                disabled: isChangingPush.value,
                onClick: enablePush
              }, {
                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                  _createTextVNode(" Enable browser alerts ", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["disabled"]))
            : (_openBlock(), _createBlock(_unref(Button), {
                key: 1,
                variant: "soft",
                tone: "neutral",
                size: "sm",
                disabled: isChangingPush.value,
                onClick: disablePush
              }, {
                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                  _createTextVNode(" Disable on this device ", -1 /* CACHED */)
                ]))]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["disabled"]))
        ]),
        _createElementVNode("p", _hoisted_12, [
          _cache[18] || (_cache[18] = _createTextVNode("Browser alerts: ", -1 /* CACHED */)),
          _createElementVNode("strong", null, _toDisplayString(pushPermissionLabel.value), 1 /* TEXT */)
        ]),
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(notificationPreferenceOptions, (option) => {
          return _createElementVNode("label", {
            key: option.key,
            class: "settings-notification-toggle"
          }, [
            _createElementVNode("span", null, [
              _createElementVNode("strong", null, _toDisplayString(option.label), 1 /* TEXT */),
              _createElementVNode("small", null, _toDisplayString(option.description), 1 /* TEXT */)
            ]),
            _createElementVNode("input", {
              type: "checkbox",
              checked: notificationPreferences.value[option.key],
              disabled: isSavingNotifications.value,
              onChange: ($event: any) => (toggleNotificationPreference(option.key, $event))
            }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_13)
          ])
        }), 64 /* STABLE_FRAGMENT */))
      ])
    ]),
    (hasApiAccess.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _cache[21] || (_cache[21] = _createElementVNode("div", null, [
              _createElementVNode("h2", null, "API keys"),
              _createElementVNode("p", null, "Use named keys for external tools such as Postman. Raw tokens are shown only once.")
            ], -1 /* CACHED */)),
            _createVNode(_unref(Button), {
              variant: "soft",
              tone: "primary",
              size: "sm",
              disabled: !hasApiAccess.value || isLoadingKeys.value,
              onClick: loadApiKeys
            }, {
              leading: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                _createElementVNode("i", {
                  class: "material-icons",
                  "aria-hidden": "true"
                }, "refresh", -1 /* CACHED */)
              ]))]),
              default: _withCtx(() => [
                _cache[20] || (_cache[20] = _createTextVNode(" Reload ", -1 /* CACHED */))
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["disabled"])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("button", {
              type: "button",
              role: "tab",
              "aria-selected": apiKeyView.value === 'active',
              class: _normalizeClass({ active: apiKeyView.value === 'active' }),
              onClick: _cache[3] || (_cache[3] = ($event: any) => (apiKeyView.value = 'active'))
            }, [
              _cache[22] || (_cache[22] = _createTextVNode(" Active ", -1 /* CACHED */)),
              _createElementVNode("span", null, _toDisplayString(activeApiKeys.value.length), 1 /* TEXT */)
            ], 10 /* CLASS, PROPS */, _hoisted_17),
            _createElementVNode("button", {
              type: "button",
              role: "tab",
              "aria-selected": apiKeyView.value === 'archived',
              class: _normalizeClass({ active: apiKeyView.value === 'archived' }),
              onClick: _cache[4] || (_cache[4] = ($event: any) => (apiKeyView.value = 'archived'))
            }, [
              _cache[23] || (_cache[23] = _createTextVNode(" Archived ", -1 /* CACHED */)),
              _createElementVNode("span", null, _toDisplayString(archivedApiKeys.value.length), 1 /* TEXT */)
            ], 10 /* CLASS, PROPS */, _hoisted_18)
          ]),
          (newToken.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _createElementVNode("div", null, [
                  _cache[24] || (_cache[24] = _createElementVNode("span", null, "Copy this token now. It will not be shown again.", -1 /* CACHED */)),
                  _createElementVNode("code", null, _toDisplayString(newToken.value), 1 /* TEXT */)
                ]),
                _createVNode(_unref(Button), {
                  variant: "solid",
                  tone: "primary",
                  size: "sm",
                  onClick: copyToken
                }, {
                  leading: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createElementVNode("i", {
                      class: "material-icons",
                      "aria-hidden": "true"
                    }, "content_copy", -1 /* CACHED */)
                  ]))]),
                  default: _withCtx(() => [
                    _cache[26] || (_cache[26] = _createTextVNode(" Copy ", -1 /* CACHED */))
                  ]),
                  _: 1 /* STABLE */
                })
              ]))
            : _createCommentVNode("v-if", true),
          _createElementVNode("form", {
            class: "settings-api-form",
            onSubmit: _withModifiers(createKey, ["prevent"])
          }, [
            _createElementVNode("label", _hoisted_20, [
              _cache[27] || (_cache[27] = _createElementVNode("span", null, "Key name", -1 /* CACHED */)),
              _createVNode(_unref(Input), {
                modelValue: newKeyName.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((newKeyName).value = $event)),
                variant: "form",
                placeholder: "Postman testing",
                maxlength: "80",
                disabled: !hasApiAccess.value
              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
            ]),
            _createElementVNode("label", _hoisted_21, [
              _cache[28] || (_cache[28] = _createElementVNode("span", null, "Calls/min", -1 /* CACHED */)),
              _createVNode(_unref(Input), {
                modelValue: rateLimitPerMinute.value,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((rateLimitPerMinute).value = $event)),
                type: "number",
                variant: "form",
                disabled: !hasApiAccess.value
              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
            ]),
            _createElementVNode("label", _hoisted_22, [
              _cache[29] || (_cache[29] = _createElementVNode("span", null, "Calls/day", -1 /* CACHED */)),
              _createVNode(_unref(Input), {
                modelValue: rateLimitPerDay.value,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((rateLimitPerDay).value = $event)),
                type: "number",
                variant: "form",
                disabled: !hasApiAccess.value
              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
            ]),
            _createVNode(_unref(Button), {
              type: "submit",
              tone: "primary",
              disabled: !hasApiAccess.value || isCreatingKey.value || !newKeyName.value.trim()
            }, {
              leading: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                _createElementVNode("i", {
                  class: "material-icons",
                  "aria-hidden": "true"
                }, "add", -1 /* CACHED */)
              ]))]),
              default: _withCtx(() => [
                _cache[31] || (_cache[31] = _createTextVNode(" Create key ", -1 /* CACHED */))
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["disabled"])
          ], 32 /* NEED_HYDRATION */),
          (isLoadingKeys.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [...(_cache[32] || (_cache[32] = [
                _createElementVNode("span", {
                  class: "settings-spinner",
                  "aria-hidden": "true"
                }, null, -1 /* CACHED */),
                _createTextVNode(" Loading API keys ", -1 /* CACHED */)
              ]))]))
            : (visibleApiKeys.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(apiKeyView.value === 'active' ? 'No active API keys.' : 'No archived API keys.'), 1 /* TEXT */))
              : (_openBlock(), _createElementBlock("div", _hoisted_25, [
                  _createElementVNode("table", _hoisted_26, [
                    _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _cache[33] || (_cache[33] = _createElementVNode("th", null, "Name", -1 /* CACHED */)),
                        _cache[34] || (_cache[34] = _createElementVNode("th", null, "Status", -1 /* CACHED */)),
                        _cache[35] || (_cache[35] = _createElementVNode("th", null, "Token ref", -1 /* CACHED */)),
                        _cache[36] || (_cache[36] = _createElementVNode("th", null, "Rate limit", -1 /* CACHED */)),
                        _cache[37] || (_cache[37] = _createElementVNode("th", null, "Last used", -1 /* CACHED */)),
                        _cache[38] || (_cache[38] = _createElementVNode("th", null, "Requests", -1 /* CACHED */)),
                        (apiKeyView.value === 'active')
                          ? (_openBlock(), _createElementBlock("th", _hoisted_27, "Actions"))
                          : _createCommentVNode("v-if", true)
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleApiKeys.value, (key) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: key.keyId
                        }, [
                          _createElementVNode("td", null, [
                            _createElementVNode("strong", null, _toDisplayString(key.name), 1 /* TEXT */),
                            _createElementVNode("span", null, _toDisplayString(formatDate(key.createdAt)), 1 /* TEXT */)
                          ]),
                          _createElementVNode("td", null, [
                            _createVNode(_unref(Tag), {
                              tone: key.status === 'active' ? 'success' : 'danger',
                              size: "sm"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(key.status), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["tone"])
                          ]),
                          _createElementVNode("td", null, [
                            _createElementVNode("code", null, _toDisplayString(key.tokenPrefix) + "..." + _toDisplayString(key.tokenSuffix), 1 /* TEXT */)
                          ]),
                          _createElementVNode("td", null, _toDisplayString(key.rateLimit.perMinute) + "/min · " + _toDisplayString(key.rateLimit.perDay) + "/day", 1 /* TEXT */),
                          _createElementVNode("td", null, _toDisplayString(formatDate(key.lastUsedAt)), 1 /* TEXT */),
                          _createElementVNode("td", null, _toDisplayString(key.requestCount), 1 /* TEXT */),
                          (apiKeyView.value === 'active')
                            ? (_openBlock(), _createElementBlock("td", _hoisted_28, [
                                _createVNode(_unref(Button), {
                                  variant: "ghost",
                                  tone: "primary",
                                  size: "sm",
                                  disabled: key.status !== 'active' || actionKeyId.value === key.keyId,
                                  onClick: ($event: any) => (rotateKey(key.keyId))
                                }, {
                                  default: _withCtx(() => [...(_cache[39] || (_cache[39] = [
                                    _createTextVNode(" Rotate ", -1 /* CACHED */)
                                  ]))]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["disabled", "onClick"]),
                                _createVNode(_unref(Button), {
                                  variant: "ghost",
                                  tone: "danger",
                                  size: "sm",
                                  disabled: key.status !== 'active' || actionKeyId.value === key.keyId,
                                  onClick: ($event: any) => (deleteKey(key.keyId))
                                }, {
                                  default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                                    _createTextVNode(" Revoke ", -1 /* CACHED */)
                                  ]))]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["disabled", "onClick"])
                              ]))
                            : _createCommentVNode("v-if", true)
                        ]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ])
                  ])
                ]))
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})